import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getLoginUrl } from "@/const";
import { soundManager } from "@/lib/soundManager";
import { Mail, Calendar, Shield, Volume2, VolumeX, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import ProfilePictureUpload from "@/components/ProfilePictureUpload";

export default function Profile() {
  const { user, isAuthenticated, loading } = useAuth();
  const [soundEnabled, setSoundEnabled] = useState(soundManager.isEnabled());
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Login Required</CardTitle>
            <CardDescription>Please login to view your profile</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={getLoginUrl()}>Login with Gmail</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSoundToggle = (enabled: boolean) => {
    setSoundEnabled(enabled);
    soundManager.setEnabled(enabled);
    if (enabled) {
      soundManager.playClick();
      toast.success("Sound effects enabled");
    } else {
      toast.success("Sound effects disabled");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-blue-950/20">
      {/* Back Button */}
      <div className="sticky top-0 z-50 bg-card/50 backdrop-blur-lg border-b border-border">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              soundManager.playClick();
              setLocation("/");
            }}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-primary to-amber-300 bg-clip-text text-transparent">
          Profile Settings
        </h1>

        {/* Profile Information */}
        <Card className="mb-6 bg-gradient-to-br from-card to-card/50 border-primary/20">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your account details from OAuth login</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <ProfilePictureUpload
                currentPicture={user.profilePicture}
                userName={user.name}
                onUploadSuccess={() => {
                  // Refresh user data
                  window.location.reload();
                }}
              />
              <div>
                <p className="text-lg font-semibold">{user.name || "Anonymous User"}</p>
                <p className="text-sm text-muted-foreground">
                  {user.loginMethod || "OAuth User"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click photo to change
                </p>
              </div>
            </div>

            {/* Email */}
            {user.email && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium">{user.email}</p>
                </div>
              </div>
            )}

            {/* Role */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <Shield className="w-5 h-5 text-primary" />
              <div>
                <Label className="text-xs text-muted-foreground">Role</Label>
                <p className="text-sm font-medium capitalize">{user.role}</p>
              </div>
            </div>

            {/* Member Since */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <Calendar className="w-5 h-5 text-primary" />
              <div>
                <Label className="text-xs text-muted-foreground">Member Since</Label>
                <p className="text-sm font-medium">
                  {new Date(user.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            {/* Last Sign In */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
              <Calendar className="w-5 h-5 text-primary" />
              <div>
                <Label className="text-xs text-muted-foreground">Last Sign In</Label>
                <p className="text-sm font-medium">
                  {new Date(user.lastSignedIn).toLocaleString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Settings */}
        <Card className="bg-gradient-to-br from-card to-card/50 border-primary/20">
          <CardHeader>
            <CardTitle>App Settings</CardTitle>
            <CardDescription>Customize your app experience</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sound Effects Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
              <div className="flex items-center gap-3">
                {soundEnabled ? (
                  <Volume2 className="w-5 h-5 text-primary" />
                ) : (
                  <VolumeX className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <Label htmlFor="sound-toggle" className="text-sm font-medium cursor-pointer">
                    Sound Effects
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Play sounds for button clicks and actions
                  </p>
                </div>
              </div>
              <Switch
                id="sound-toggle"
                checked={soundEnabled}
                onCheckedChange={handleSoundToggle}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
